import { formatMoney } from "../../data/pricingContent";

function DetailRow({ label, value, mono }) {
    if (!value) return null;
    return (
        <div className="flex justify-between items-start gap-4 py-3 border-b border-white/[0.06] last:border-0 text-sm">
            <span className="text-zinc-500 shrink-0">{label}</span>
            <span
                className={`text-zinc-100 text-right font-medium break-all ${mono ? "font-code text-xs text-zinc-300" : ""}`}
            >
                {value}
            </span>
        </div>
    );
}

function formatBillingPeriod(period) {
    if (!period) return null;
    const normalized = String(period).toLowerCase();
    if (normalized === "year" || normalized === "yearly") return "Annual";
    if (normalized === "month" || normalized === "monthly") return "Monthly";
    return period;
}

export default function PaymentDetailsCard({ data }) {
    if (!data) return null;

    const quote = data.quote ?? null;
    const currency = data.currency || quote?.currency || "INR";
    const amount =
        data.amount != null
            ? formatMoney(Number(data.amount), currency)
            : quote?.amount != null
              ? formatMoney(Number(quote.amount), currency)
              : null;

    const planId = data.plan_id || quote?.plan_id;
    const planLabel = planId
        ? String(planId).charAt(0).toUpperCase() + String(planId).slice(1)
        : null;

    const licenseCount = Number(
        data.license_count || quote?.license_count || 0,
    );
    const perLicense = Number(quote?.per_license || 0);
    const billingPeriod = formatBillingPeriod(
        data.billing_period || quote?.billing_period,
    );
    const billingUnit =
        String(data.billing_period || quote?.billing_period || "").toLowerCase() ===
        "year"
            ? "yr"
            : "mo";

    const summaryTitle =
        planLabel && billingPeriod
            ? `${planLabel} · ${billingPeriod}`
            : planLabel || billingPeriod;

    const lineItemLabel =
        licenseCount > 0 && perLicense > 0
            ? `${licenseCount} licenses × ${formatMoney(perLicense, currency)} / ${billingUnit}`
            : licenseCount > 0
              ? `${licenseCount} seats`
              : null;

    return (
        <div
            className="mt-8 rounded-2xl border border-white/[0.08] bg-black/30 overflow-hidden"
            data-testid="payment-details-card"
        >
            <div className="px-5 py-4 border-b border-white/[0.06]">
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-code">
                    Transaction summary
                </p>
                {summaryTitle && (
                    <p className="mt-3 text-sm font-medium text-zinc-200">
                        {summaryTitle}
                    </p>
                )}
                {lineItemLabel && (
                    <div className="mt-3 flex items-start justify-between gap-4 text-sm">
                        <span className="text-zinc-500">{lineItemLabel}</span>
                        {amount && (
                            <span className="font-display text-zinc-100 tabular-nums shrink-0">
                                {amount}
                            </span>
                        )}
                    </div>
                )}
                {amount && (
                    <div className="mt-4 flex items-center justify-between gap-4 border-t border-white/[0.06] pt-4">
                        <span className="text-sm font-medium text-zinc-300">
                            Total paid
                        </span>
                        <span className="font-display text-2xl text-white tabular-nums">
                            {amount}
                        </span>
                    </div>
                )}
            </div>
            <div className="px-5 py-2 border-b border-white/[0.06]">
                <p className="py-3 text-xs uppercase tracking-[0.2em] text-zinc-500 font-code">
                    Receipt
                </p>
                <DetailRow label="Reference" value={data.reference_number || data.txnid} mono />
                <DetailRow label="Payment ID" value={data.payment_id || data.mihpayid} mono />
                <DetailRow label="Email" value={data.email} />
                <DetailRow label="Company" value={data.company} />
                <DetailRow label="Method" value={data.payment_method} />
                <DetailRow
                    label="Provider"
                    value={
                        data.provider
                            ? String(data.provider).toUpperCase()
                            : null
                    }
                />
                <DetailRow
                    label="Session ID"
                    value={data.payments_session_id}
                    mono
                />
                {data.paid_at && (
                    <DetailRow
                        label="Paid at"
                        value={new Date(data.paid_at).toLocaleString()}
                    />
                )}
            </div>
            {(data.zoho_fetch_error || data.payu_fetch_error) && (
                <p className="px-5 py-3 text-xs text-amber-400/90 bg-amber-500/5 border-t border-amber-500/10">
                    {data.zoho_fetch_error || data.payu_fetch_error}
                </p>
            )}
        </div>
    );
}
